import { Fragment } from 'react'
import styled from 'styled-components/macro'

import { Layout } from '@loadsmart/loadsmart-ui'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'
import { Icon, Text } from '@loadsmart/miranda-react'

import { type ShippingItem } from 'core/shipping-items/domain/ShippingItem'
import { StopShippingItemSummary } from 'core/shipping-items/ui/StopShippingItemSummary'

import { formatZonedDate } from 'legacy/utils/date/formatZonedDate'
import { phoneFilter } from 'legacy/utils/phoneFilter'

import { isPortFacility, isWarehouseFacility } from '../domain/Facility'
import { type Carrier, type Stop } from '../domain/Shipment'
import { formatValue } from '../presenters/shipmentPresenter'
import { getContactName, getFormattedCity } from '../presenters/stopPresenter'
import { AppointmentCard } from './AppointmentCard/AppointmentCard'

const StyledStack = styled(Layout.Stack)`
  padding: 20px 55px 20px;
  position: relative;

  &:before,
  &:after {
    position: absolute;
    top: 0;
    left: 25px;
    width: 3px;
    height: calc(100% + 2px);
    background-color: ${getToken('color-neutral-light')};
    content: '';
  }

  &:before {
    top: -20px;
    z-index: 20;
    height: calc(66% + 2px);
  }

  &.is-previous {
    &:after,
    &:before {
      background-color: ${getToken('color-primary')};
    }
  }

  &.is-current:before {
    background-color: ${getToken('color-primary')};
  }
`
export type ShipmentStopState = 'is-current' | 'is-previous' | undefined

export type ShipmentStopInfoProps = {
  shipmentUUID: string
  shipmentCarrier: Carrier
  stop: Stop
  status: string
  state: ShipmentStopState
  lookupShipment: (uuid: string) => Promise<void>
  isDrayageShipment: boolean
  shippingItems?: Array<ShippingItem>
}

export function ShipmentStopInfo({
  shipmentUUID,
  shipmentCarrier,
  stop,
  status,
  state,
  lookupShipment,
  isDrayageShipment,
  shippingItems,
}: ShipmentStopInfoProps) {
  const isWarehouse = isWarehouseFacility(stop.facility)

  const showContactInfo = !isDrayageShipment || (isDrayageShipment && isWarehouse)

  return (
    <StyledStack space="3xl" className={`${state}`}>
      <Layout.Stack space="xs">
        <Text variant="body-sm-bold" color="color-text-tertiary">
          {status}
        </Text>
        {isDrayageShipment && isPortFacility(stop.facility) && (
          <Icon data-testid="terminal-facility-icon" name="terminal" size="24px" />
        )}
        {isDrayageShipment && isWarehouse && (
          <Icon data-testid="warehouse-facility-icon" name="warehouse" size="24px" />
        )}
        <Layout.Stack>
          <Layout.Stack space="xs">
            {stop.facility ? (
              <>
                <Text variant="heading-sm">{stop.facility.companyName}</Text>
                <Text variant="body-md">{stop.facility.address}</Text>
                <Text variant="body-md">{getFormattedCity(stop)}</Text>
              </>
            ) : (
              <>
                <Text variant="body-md">{stop.address}</Text>
                <Text variant="body-md">{getFormattedCity(stop)}</Text>
              </>
            )}
          </Layout.Stack>
          <Layout.Group justify="space-between">
            <Layout.Stack space="xs">
              <Text variant="body-sm" color="color-text-tertiary">
                Stop Date
              </Text>
              {stop.date && (
                <Text variant="body-md">
                  {formatZonedDate(stop.date, stop.timezone, 'EEE, MMM d yyyy')}
                </Text>
              )}
            </Layout.Stack>
            {showContactInfo && (
              <Fragment>
                <Layout.Stack space="xs">
                  <Text variant="body-sm" color="color-text-tertiary">
                    Appointment
                  </Text>
                  {stop.appointmentMode && <Text variant="body-md">{stop.appointmentMode}</Text>}
                </Layout.Stack>
                <Layout.Stack space="xs">
                  <Text variant="body-sm" color="color-text-tertiary">
                    Contact Name
                  </Text>
                  <Text variant="body-md">{getContactName(stop)}</Text>
                </Layout.Stack>
                <Layout.Stack space="xs">
                  <Text variant="body-sm" color="color-text-tertiary">
                    Phone Number/Email
                  </Text>
                  <Layout.Stack space="2xs">
                    {stop.contact && (
                      <Fragment>
                        <Text variant="body-md">{phoneFilter(stop.contact.phoneNumber)}</Text>
                        <Text variant="body-md">{stop.contact.email}</Text>
                      </Fragment>
                    )}
                  </Layout.Stack>
                </Layout.Stack>
              </Fragment>
            )}
          </Layout.Group>
        </Layout.Stack>
      </Layout.Stack>
      <AppointmentCard
        shipmentUUID={shipmentUUID}
        shipmentCarrier={shipmentCarrier}
        stopIndex={stop.stopIndex}
        stopDate={stop.date}
        appointment={stop.appointment}
        facilityName={stop.facility?.companyName}
        facilityContact={stop.contact}
        warehouseUUID={stop.facility?.warehouseUuid}
        onConfirm={lookupShipment}
        facility={stop.facility}
      />
      <Layout.Stack space="xs">
        <Text variant="body-sm" color="color-text-tertiary">
          Instructions
        </Text>
        <Text variant="body-md">{formatValue(stop.notes)}</Text>
      </Layout.Stack>
      {shippingItems && (
        <StopShippingItemSummary
          stopType={stop.stopType}
          shippingItems={shippingItems}
          stopIndex={stop.stopIndex}
        />
      )}
    </StyledStack>
  )
}
